<template>
  <BaseCard class="max-h-[720px] overflow-x-auto">
    <LoadingWrapper
      v-if="meteringPoint"
      :error="loadingFailed"
      :loading="loading"
    >
      <BaseTable
        :columns="columns"
        :rows="rows"
        :precision="PRECISION"
        class="min-h-24"
      >
        <!-- Add header tooltips -->
        <template
          v-for="({ label, wikiPath }, key, index) in columns"
          :key="label"
          #[key]="{ header }"
        >
          {{ header }}
          <WikiTooltipComponent
            v-if="wikiPath"
            class="top-0.5"
            :path="wikiPath"
            :position="
              index === Object.keys(columns).length - 1
                ? 'bottom left'
                : 'bottom'
            "
          />
        </template>
        <!-- Bold total column -->
        <template #TOTAL-cell="{ row }">
          <div class="font-bold text-gray-500">
            {{ formatNumber(row.TOTAL, PRECISION) }}
          </div>
        </template>
        <template #TOTAL-summary="{ row }">
          <div class="font-bold text-gray-500">
            {{ formatNumber(row.TOTAL, PRECISION) }}
          </div>
        </template>
        <!-- Columns with extra precision -->
        <template
          v-for="column in precisionColumns"
          :key="column"
          #[`${column}-cell`]="{ row }"
        >
          {{
            Object.hasOwn(row, column)
              ? formatNumber(row[column], PRECISION_EXTRA)
              : ""
          }}
        </template>
        <template
          v-for="column in precisionColumns"
          :key="column"
          #[`${column}-summary`]="{ row }"
        >
          {{
            Object.hasOwn(row, column)
              ? formatNumber(row[column], PRECISION_EXTRA)
              : ""
          }}
        </template>
        <!-- Italic summary cell -->
        <template #datetime-summary="{ row }">
          <span class="italic">
            {{ row.datetime }}
          </span>
        </template>
        <!-- Format dates and add badges to datetime cells -->
        <template #datetime-cell="{ row }">
          <div class="flex gap-1">
            <span>{{ format(row.datetime, dateFormat[step]) }}</span>
            <BaseBadge
              v-if="isToday(row.datetime) && step !== 'minute'"
              color="primary"
            >
              Vandaag
            </BaseBadge>
            <BaseBadge
              v-else-if="!row.stable"
              class="!bg-energy-50 !text-energy-900"
            >
              Voorlopig
            </BaseBadge>
          </div>
        </template>
      </BaseTable>
    </LoadingWrapper>
    <div v-else class="flex items-center justify-center p-4 italic md:p-6">
      <p>Maak een selectie om gegevens te kunnen bekijken</p>
    </div>
  </BaseCard>
</template>

<script setup>
import { computed, ref, watch } from "vue"
import {
  differenceInCalendarDays,
  differenceInCalendarMonths,
  format,
  isToday,
} from "date-fns"
import { debounce, DEBOUNCE_DELAY } from "@/helpers/debounce.js"
import { formatNumber } from "@/services/formatterService.js"
import useNotificationStore from "@/stores/notificationStore.js"
import {
  BaseTable,
  BaseBadge,
  BaseCard,
  LoadingWrapper,
} from "@repowerednl/ui-component-library"
import WikiTooltipComponent from "@/components/information/WikiTooltipComponent.vue"

const props = defineProps({
  /**
   * Can be either an asset or a portfolio, but no data is shown for portfolios.
   */
  meteringPoint: {
    type: Object,
    default: null,
  },
  /**
   * Array of two Date objects; the last day is included in the range.
   */
  dateRange: {
    type: Array,
    required: true,
  },
  /**
   * Determines which data series are shown/hidden.
   */
  mode: {
    required: true,
    validator: (value) => ["energy", "finance", "both"].includes(value),
  },
})

const DAYS_MINUTE = 5
const DAYS_DAY = 32
const MONTHS_YEAR = 25
const PRECISION = 2
const PRECISION_EXTRA = 5

const notificationStore = useNotificationStore()
const recordsData = ref()
const loading = ref(false)
const loadingFailed = ref(false)

/**
 * Columns that are hidden in the different modes.
 */
const modeHiddenColumns = {
  energy: ["€", "€/kWh"],
  finance: ["kWh"],
  both: [],
}

const freq = {
  minute: "15min",
  day: "1D",
  month: "MS",
  year: "YS",
}

const dateFormat = {
  minute: "PPp",
  day: "PP",
  month: "MMMM y",
  year: "y",
}

const step = computed(() => {
  const days =
    1 + differenceInCalendarDays(props.dateRange[1], props.dateRange[0])
  const months =
    1 + differenceInCalendarMonths(props.dateRange[1], props.dateRange[0])
  if (days < DAYS_MINUTE) {
    return "minute"
  } else if (days < DAYS_DAY) {
    return "day"
  } else if (months < MONTHS_YEAR) {
    return "month"
  } else {
    return "year"
  }
})

const columns = computed(() => {
  if (!recordsData.value) {
    return {}
  }
  // Always show the datetime column
  const entries = [["datetime", { label: "Datum" }]]

  // Order types by their order property
  const sortedTypes = Object.entries(recordsData.value.types).sort((a, b) => {
    return a[1].order - b[1].order
  })

  // Construct input for the BaseTable component
  for (const [type, spec] of sortedTypes) {
    if (!isHiddenUnit(spec.unit) && !spec.is_hidden) {
      // Add the current column
      entries.push([type, buildDataColumn(spec)])

      // #TODO(REP-4240) manually add these keys until they are returned from API
      // Add the total columns equal to the invoice heading
      if (type === "DAY_AHEAD_TAKE_OFF_RESULT") {
        entries.push([
          "DAY_AHEAD_TOTAL_RESULT",
          {
            label: `Resultaat dagmarkt (${spec.unit ?? "€"})`,
            group: spec.category?.nl ?? "Dagmarkt",
            wikiPath: null,
            classes: "text-right font-bold text-primary-600",
          },
        ])
      }

      if (type === "ALLOCATION_POINT_IMBALANCE_TAKE_OFF_RESULT") {
        entries.push([
          "ALLOCATION_POINT_TOTAL_RESULT",
          {
            label: `Resultaat onbalansmarkt (${spec.unit ?? "€"})`,
            group: spec.category?.nl ?? "Onbalansmarkt",
            wikiPath: null,
            classes: "text-right font-bold text-primary-600",
          },
        ])
      }
    }
  }
  return Object.fromEntries(entries)
})

/**
 * Collects all columns that should be formatted with extra precision.
 */
const precisionColumns = computed(() =>
  recordsData.value
    ? Object.keys(recordsData.value.types).filter(
        (type) => recordsData.value.types[type].is_high_precision,
      )
    : [],
)

/**
 * Flattens a record entry to pass to the BaseTable component's 'row' prop.
 * Before:
 *   [
 *     "2000-01-01T00:00:00.000Z",
 *     {
 *       "data": {
 *         "Type A": {
 *           "value": 0,
 *           "stable": true
 *         },
 *         "Type B": {
 *           "value": 0,
 *           "stable": false
 *         },
 *       },
 *     },
 *   ]
 * After:
 *   {
 *     _id: "cell",
 *     datetime: new Date("2000-01-01T00:00:00.000Z"),
 *     stable: false,
 *     "Type A": 0,
 *     "Type B": 0,
 *   }
 */
function flattenRecord(entry) {
  const [key, { data }] = entry
  const values = {}
  let stable = true
  for (const [recordType, content] of Object.entries(data)) {
    values[recordType] = content.value
    stable = stable && content.stable
  }

  // #TODO(REP-4240) manually add these sums until they are returned from API
  const feedInDayAhead = values["DAY_AHEAD_FEED_IN_RESULT"] ?? 0
  const takeOffDayAhead = values["DAY_AHEAD_TAKE_OFF_RESULT"] ?? 0
  values["DAY_AHEAD_TOTAL_RESULT"] = feedInDayAhead + takeOffDayAhead

  const feedInImbalance =
    values["ALLOCATION_POINT_IMBALANCE_FEED_IN_RESULT"] ?? 0
  const takeOffImbalance =
    values["ALLOCATION_POINT_IMBALANCE_TAKE_OFF_RESULT"] ?? 0
  values["ALLOCATION_POINT_TOTAL_RESULT"] = feedInImbalance + takeOffImbalance

  return {
    _id: key === "Totaal" ? "summary" : "cell",
    datetime: key === "Totaal" ? key : new Date(key),
    stable,
    ...values,
  }
}

const rows = computed(() => {
  if (!recordsData.value) {
    return []
  }
  const { records, totals } = recordsData.value
  return [
    flattenRecord(["Totaal", totals]),
    ...Object.entries(records).map(flattenRecord),
  ]
})

function onNewRecords(newRecords) {
  recordsData.value = newRecords
  loading.value = false
}

function onLoadRecordsFailed(error) {
  loadingFailed.value = true
  loading.value = false
  notificationStore.pushError(
    "Fout bij het ophalen van resultaten",
    `De resultaten konden niet worden opgehaald. Probeer het later opnieuw. (code: ${error?.code})`,
    "load-records-error",
  )
}

function isHiddenUnit(unit) {
  return modeHiddenColumns[props.mode].includes(unit)
}

function buildDataColumn(spec) {
  const billable = spec.billable && spec.unit === "€"
  return {
    label: `${spec.label?.nl} (${spec.unit})`,
    group: spec.category?.nl,
    wikiPath: spec.wiki_path?.nl,
    classes: [
      "text-right tabular-nums",
      billable ? "[&:is(td)]:font-medium text-primary-600" : "",
      spec.is_total_result ? "font-bold text-primary-600 !bg-primary-50" : "",
    ].join(" "),
  }
}

const loadData = debounce(() => {
  props.meteringPoint?.loadResults(
    freq[step.value],
    props.dateRange,
    false,
    onNewRecords,
    onLoadRecordsFailed,
  )
}, DEBOUNCE_DELAY)

watch(
  [() => props.meteringPoint, () => props.dateRange],
  () => {
    recordsData.value = null
    if (!props.meteringPoint) {
      return
    }
    loading.value = true
    loadingFailed.value = false
    loadData()
  },
  { immediate: true },
)
</script>
